import { useParams } from "react-router-dom";

import {
  useTenantDetails,
  useTenantStyles,
} from "../../../hooks/queries/admin/hospitals";
import {
  AdminBreadcrumb,
  ErrorMessage,
  LoadingOverlay,
  PageContainer,
  PageHeader,
  PageIntro,
  PageTitle,
} from "../shared";
import { TenantLogoCard } from "./TenantLogoCard";
import { TenantRecordForm } from "./TenantRecordForm";
import { TenantStylesEditor } from "./TenantStylesEditor";

/**
 * Tenant details route (EN-5154) — the portal port of Retool's
 * `page_TenantDetails`. Retool served the page and an identical
 * `moduleTenantDetails`; the page is the one its sidebar opens (by uuid), and
 * the module is a copy, so this implements it once.
 */
export function AdminTenantDetails() {
  const { tenantId } = useParams<{ tenantId: string }>();

  const { tenant, isLoading, isError } = useTenantDetails(tenantId);
  const { styles } = useTenantStyles(tenantId);

  const currentLabel = tenant?.name || "Tenant";
  document.title = `${currentLabel} - Admin`;

  return (
    <PageContainer>
      <PageIntro>
        <AdminBreadcrumb
          items={[
            { label: "Tenants", to: "/admin/tenants" },
            { label: currentLabel },
          ]}
        />
        <PageHeader>
          <PageTitle>{currentLabel}</PageTitle>
        </PageHeader>
      </PageIntro>

      {isLoading && <LoadingOverlay>Loading tenant...</LoadingOverlay>}

      {!isLoading && (isError || !tenant) && (
        <ErrorMessage>
          Failed to load this tenant. Try refreshing the page.
        </ErrorMessage>
      )}

      {!isLoading && tenant && (
        <>
          <TenantRecordForm tenant={tenant} />
          <TenantLogoCard tenant={tenant} />
          {styles && (
            <TenantStylesEditor tenantId={tenant.id} styles={styles} />
          )}
        </>
      )}
    </PageContainer>
  );
}
